import React, { useState, useEffect, useCallback, useRef } from "react";
import ClockDisplay from "../../components/ClockDisplay/ClockDisplay";
import CompetitiveClockDisplay from "../../components/ClockDisplay/CompetitiveClockDisplay/CompetitiveClockDisplay";
import FeedbackDisplay from "../../components/FeedbackDisplay/FeedbackDisplay";
import Modal from "../../components/Modal/Modal";
import RankedPromptPopup from "../../components/Modal/RankedPromptPopup";
import getRandomPosition from "../../utils/positions";
import Tabs from "../../components/Tabs";

import "../../App.css";
import "./CompetitiveMode.css";
// Constants
const COUNTDOWN = 60; // Countdown time in seconds
//Scoring system
// Point scoring system as follows:

// +100 points if you get the exact time right
// +30 points if you get within 4 minutes of the right time
// +10 points if you are within 5-10 minutes of the right time
// -50 points if you are 11+ minutes off

// This is the scoring for a 30-second mode. If it's 60, halve all point values and if it's 15 double them.

const POINTS_EXACT_TIME = 100;
const POINTS_WITHIN_4_MINS = 30;
const POINTS_WITHIN_5_10_MINS = 10;
const POINTS_11_PLUS_MINS = 50;

function CompetitiveMode() {
  const [targetTime, setTargetTime] = useState(Date.now());
  const [userInput, setUserInput] = useState("");
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timer, setTimer] = useState(COUNTDOWN);
  const [gameOver, setGameOver] = useState(false);
  const [gameStatus, setGameStatus] = useState("waiting");
  // gameStatus: "waiting" | "started" | "ended"
  const [gameStartTime, setGameStartTime] = useState(Date.now());
  const [showModal, setShowModal] = useState(false);
  const [isPopupOpen, setPopupOpen] = useState(
    localStorage.getItem("hideRankedPopup") !== "true"
  );
  const [username, setUsername] = useState(
    localStorage.getItem("username") || "AnonymousUser"
  );
  const [inputStartTime, setInputStartTime] = useState(null);
  const [multiRoundTracker, setMultiRoundTracker] = useState(
    JSON.parse(localStorage.getItem("monkeyClock_rounds") || "[]")
  );
  const [avgTimeDifference, setAvgTimeDifference] = useState(0);
  const [compRoundTracker, setCompRoundTracker] = useState([]);
  const [scoreSubmitted, setScoreSubmitted] = useState(false);
  const [userIPInfo, setUserIPInfo] = useState({});
  // compRoundTracker will have: {attempt: 0, time, input, difference, points, score}

  const feedbackRef = React.useRef();
  const attemptsRef = useRef(0);

  // Update attemptsRef whenever attempts changes
  useEffect(() => {
    attemptsRef.current = attempts;
  }, [attempts]);

  const containerStyle = {
    filter: gameStatus === "waiting" ? "blur(5px)" : "none",
  };

  function handleGameOver() {
    setGameOver(true);
    setGameStatus("ended");
    setShowModal(true);
    setTimer(COUNTDOWN);
  }

  useEffect(() => {
    fetch("https://ipapi.co/json/")
      .then((res) => res.json())
      .then((data) => setUserIPInfo(data))
      .catch((err) => console.error("Error fetching IP:", err));
  }, []);

  useEffect(() => {
    let animationFrame;
    const updateTimer = () => {
      const elapsed = (Date.now() - gameStartTime) / 1000;
      const newTimer = COUNTDOWN - elapsed;
      if (newTimer > 0) {
        setTimer(newTimer);
        animationFrame = requestAnimationFrame(updateTimer);
      } else {
        handleGameOver();
        cancelAnimationFrame(animationFrame);
      }
    };

    if (!gameOver && gameStatus === "started") {
      animationFrame = requestAnimationFrame(updateTimer);
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [gameStartTime, gameOver, gameStatus]);
  
  // Remove the inputStartTime setting from handleInputChange
  // Instead, add this useEffect to start timing when a new target time is set:
  useEffect(() => {
    setInputStartTime(Date.now());
  }, [targetTime]);

  function generateRandomTime() {
    const hours = Math.floor(Math.random() * 12) + 1; // 1 to 12
    const minutes = Math.floor(Math.random() * 60);
    return `2024-03-15T${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}:00`;
  }

  function formatTimeInput(value) {
    // Remove any non-digit characters
    const digits = value.replace(/\D/g, "");
    if (digits.length <= 2) return digits;
    if (digits.length === 3) {
      return `${digits[0]}:${digits.slice(1)}`;
    }
    return `${digits.slice(0, 2)}:${digits.slice(2, 4)}`;
  }

  function handleInputChange(e) {
    const formattedValue = formatTimeInput(e.target.value);
    // if (!inputStartTime && formattedValue.length === 1) {
    //   setInputStartTime(Date.now());
    // }
    setUserInput(formattedValue);
  }

  function handleSubmit() {
    const inputDuration = inputStartTime
      ? (Date.now() - inputStartTime) / 1000
      : 0;
      console.log("inputDuration", inputDuration);
      console.log("inputStartTime", {inputStartTime});
    setAttempts(attempts + 1);
    const [hours, minutes] = targetTime.split("T")[1].split(":");
    const correctTime = `${hours}:${minutes}`;
    const normalizeTime = (time) => {
      const [h, m] = time.split(":");
      return `${parseInt(h, 10)}:${parseInt(m, 10)}`;
    };

    const normalizedUserInput = normalizeTime(userInput);
    const normalizedCorrectTime = normalizeTime(correctTime);

    const calculateTimeDifference = (userTime, correctTime) => {
      // Split the time strings into hours and minutes
      const [userHours, userMinutes] = userTime.split(":").map(Number);
      const [correctHours, correctMinutes] = correctTime.split(":").map(Number);

      // Convert both times to minutes since midnight (00:00)
      const userTotalMinutes = userHours * 60 + userMinutes;
      const correctTotalMinutes = correctHours * 60 + correctMinutes;
      return Math.abs(userTotalMinutes - correctTotalMinutes);
    };

    const timeDifference = calculateTimeDifference(
      normalizedUserInput,
      normalizedCorrectTime
    );

    let POINTS_TO_APPLY = 0;
    if (timeDifference === 0) {
      POINTS_TO_APPLY = POINTS_EXACT_TIME;
      setCorrectAnswers(correctAnswers + 1);
      feedbackRef.current(
        "randomMessage",
        "GoodPoints",
        correctTime,
        userInput,
        POINTS_EXACT_TIME
      );
    } else if (timeDifference <= 4) {
      POINTS_TO_APPLY = POINTS_WITHIN_4_MINS;
      setCorrectAnswers(correctAnswers + 1);
      feedbackRef.current(
        "randomMessage",
        "GoodPoints",
        correctTime,
        userInput,
        POINTS_WITHIN_4_MINS
      );
    } else if (timeDifference <= 10) {
      POINTS_TO_APPLY = POINTS_WITHIN_5_10_MINS;
      feedbackRef.current(
        "randomMessage",
        "GoodPoints",
        correctTime,
        userInput,
        POINTS_WITHIN_5_10_MINS
      );
    } else {
      POINTS_TO_APPLY = -POINTS_11_PLUS_MINS;
      feedbackRef.current(
        "randomMessage",
        "PoorPoints",
        correctTime,
        userInput,
        POINTS_11_PLUS_MINS
      );
    }
    setScore(score + POINTS_TO_APPLY);

    //Remove leading zero from correct time for display purposes
    const correctTimeNoLeadingZero = correctTime.replace(/^0/, "");
    setCompRoundTracker([
      ...compRoundTracker,
      {
        attempt: attempts + 1,
        targetTime: correctTimeNoLeadingZero,
        userInput,
        difference: !isNaN(timeDifference) ? timeDifference : "?",
        points: POINTS_TO_APPLY,
        score: score + POINTS_TO_APPLY,
        inputDuration: inputDuration.toFixed(2),
      },
    ]);

    setUserInput("");
    setInputStartTime(null);
    setTargetTime(generateRandomTime());
  }

  function handleStart() {
    setScore(0);
    setAttempts(0);
    setCorrectAnswers(0);
    setTimer(COUNTDOWN);
    setGameOver(false);
    setGameStatus("started");
    setTargetTime(generateRandomTime());
    setGameStartTime(Date.now());
    setCompRoundTracker([]);
    setShowModal(false);
    setScoreSubmitted(false);
  }

  const handleFeedbackRef = useCallback((addFeedback) => {
    feedbackRef.current = addFeedback;
  }, []);

  useEffect(() => {
    if (gameStatus === "ended") {
      let totalDifference = compRoundTracker.reduce(
        (sum, attempt) =>
          sum +
          (typeof attempt.difference === "number" ? attempt.difference : 0),
        0
      );
      let avgDifference = (totalDifference / compRoundTracker.length).toFixed(
        2
      );
      setAvgTimeDifference(avgDifference);

      const roundData = {
        id: Date.now(),
        date: new Date().toISOString(),
        duration: COUNTDOWN,
        totalAttempts: attempts,
        correctAnswers,
        score,
        accuracy: attempts
          ? ((correctAnswers / attempts) * 100).toFixed(2)
          : "0.00",
        avgTimePerAttempt: attempts
          ? (COUNTDOWN / attempts).toFixed(2)
          : "0.00",
        avgDifference: avgDifference || "0.00",
        attemptDetails: compRoundTracker,
        username: username,
        ipInfo: userIPInfo,
      };

      //Only append data if there was at least 1 attempt
      if (attempts > 0) {
        const updatedMultiRoundTracker = [...multiRoundTracker, roundData];
        setMultiRoundTracker(updatedMultiRoundTracker);
        localStorage.setItem(
          "monkeyClock_rounds",
          JSON.stringify(updatedMultiRoundTracker)
        );

        fetch("https://www.rikerwilson.com/api/submitScore", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(roundData),
        })
          .then((res) => res.json())
          .then((data) => {
            console.log("Score submitted successfully", data);
            setScoreSubmitted(true);
          })
          .catch((err) => console.error("Error submitting score", err));
      }
    }
  }, [gameStatus, attempts, correctAnswers, score, compRoundTracker]);

  return (
    <div className="CompetitiveMode-container">
      {/* <div className="timer">
        Competitive Mode -- Time Left: {timer.toFixed(1)}s
      </div> */}
      <div>
        <div className="clock__text"></div>
      </div>
      <div className="CompetitiveClockDisplayContainer" style={containerStyle}>
        <CompetitiveClockDisplay targetTime={targetTime} timer={timer} />
      </div>
      {/* GAME IS WAITING FOR FIRST START */}
      {(gameStatus === "waiting" || (gameStatus === "ended" && !showModal)) && (
        <button onClick={handleStart} className="start-restart-button">
          Play Game
        </button>
      )}
      {/* GAME IS STARTED */}
      {gameStatus == "started" && (
        <div>
          <input
            type="text"
            value={userInput}
            onChange={handleInputChange}
            onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
            placeholder="HH:MM"
            className="time-input"
          />
        </div>
      )}
      <FeedbackDisplay ref={handleFeedbackRef} />
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
        <div className="round-summary">
          <h2>Round Complete!</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <h3>Total Attempts</h3>
              <p>{attempts}</p>
            </div>
            <div className="stat-item">
              <h3>Correct Answers</h3>
              <p>{correctAnswers}</p>
            </div>
            <div className="stat-item">
              <h3>Final Score</h3>
              <p>{score}</p>
            </div>
            <div className="stat-item">
              <h3>Accuracy</h3>
              <p>{((correctAnswers / attempts) * 100).toFixed(2)}%</p>
            </div>

            <div className="stat-item">
              <h3>Avg. Time per Attempt</h3>
              <p>{(COUNTDOWN / attempts).toFixed(2)}s</p>
            </div>
            <div className="stat-item">
              <h3>Avg. Time Difference</h3>
              <p>{avgTimeDifference} min</p>
            </div>
          </div>

          <button onClick={handleStart} className="start-restart-button">
            Play Again
          </button>
        </div>
      </Modal>

      {!isPopupOpen && (
        <button
          onClick={() => {
            // localStorage.setItem("hideRankedPopup", false);
            setPopupOpen(true);
          }}
          className="showPopup"
        >
          Show Briefing
        </button>
      )}

      {!gameOver && gameStatus === "started" && (
        <button onClick={handleStart} className="restart-btn">
          Reset
        </button>
      )}
      {!gameOver && gameStatus === "started" && (
        <div className="game-info-container">
          <div className="game-info-header">Game Stats</div>
          <div className="game-info">
            <div className="game-info__item">
              <span className="game-info__label">Score:</span> {score}
            </div>
            <div className="game-info__item">
              <span className="game-info__label">Attempts:</span> {attempts}
            </div>
            <div className="game-info__item">
              <span className="game-info__label">Correct:</span>
              {correctAnswers}
            </div>
          </div>
        </div>
      )}
      <RankedPromptPopup
        isOpen={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onUsernameSubmit={(name) => setUsername(name)}
      />
    </div>
  );
}

export default CompetitiveMode;
